// Derives the DARK variant of a tenant's colours. Pure + isomorphic: ThemeProvider
// calls it on the server so the dark palette ships in the same inline <style> as the
// light one, and switching modes is a single attribute flip with no JS colour math.
//
// The rules follow one principle — keep what the merchant chose unless it would be
// unreadable on a dark page:
//  - SURFACES (page, cards, header, footer, inputs) that are light are replaced by our
//    dark neutrals. A surface the merchant already made dark or saturated (a navy
//    header, a red footer) is brand identity and is kept.
//  - TEXT keeps its colour if it already reads on the dark surface (WCAG 4.5:1);
//    otherwise a chromatic colour is lightened toward white (hue survives — a brand
//    price stays "the brand price"), and a grey/black one becomes our light text.
//  - BRAND fills (buttons, add-to-cart) stay as-is when they stand out from the page
//    (3:1, the WCAG non-text threshold). Only near-invisible ones are adjusted, and only
//    then is the matching "inner" (label) colour recomputed.

export type Rgb = { r: number; g: number; b: number; a: number };

export const DARK_NEUTRALS = {
  pageBg: "#121212",
  surface: "#1c1c1c",
  raisedSurface: "#1e1e1e",
  header: "#1a1a1a",
  footer: "#161616",
  subtleSurface: "#161616",
  border: "#2e2e2e",
  text: "#ececec",
  onLight: "#121212",
  onDark: "#ffffff",
} as const;

const LIGHT_SURFACE_LUMINANCE = 0.4;
const TEXT_CONTRAST = 4.5;
const FILL_CONTRAST = 3;
const CHROMA_THRESHOLD = 0.15;

const clamp255 = (n: number) => Math.min(255, Math.max(0, Math.round(n)));

export const parseColor = (input?: string | null): Rgb | null => {
  if (!input) return null;
  const value = input.trim().toLowerCase();

  const hex = value.match(/^#([0-9a-f]{3,8})$/)?.[1];
  if (hex) {
    if (hex.length === 3 || hex.length === 4) {
      const [r, g, b, a = "f"] = hex.split("");
      return {
        r: parseInt(r + r, 16),
        g: parseInt(g + g, 16),
        b: parseInt(b + b, 16),
        a: parseInt(a + a, 16) / 255,
      };
    }
    if (hex.length === 6 || hex.length === 8) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
      };
    }
    return null;
  }

  const fn = value.match(/^rgba?\(([^)]+)\)$/)?.[1];
  if (fn) {
    const parts = fn.split(/[\s,/]+/).filter(Boolean).map(Number);
    if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
    const [r, g, b, a = 1] = parts;
    return { r: clamp255(r), g: clamp255(g), b: clamp255(b), a };
  }

  if (value === "white") return { r: 255, g: 255, b: 255, a: 1 };
  if (value === "black") return { r: 0, g: 0, b: 0, a: 1 };
  return null;
};

export const toHex = ({ r, g, b }: Rgb) =>
  `#${[r, g, b].map((n) => clamp255(n).toString(16).padStart(2, "0")).join("")}`;

export const relativeLuminance = ({ r, g, b }: Rgb) => {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

export const contrastRatio = (a: Rgb, b: Rgb) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

const chroma = ({ r, g, b }: Rgb) =>
  (Math.max(r, g, b) - Math.min(r, g, b)) / 255;

// Rounded to whole channels HERE so the contrast we measure is the contrast of the hex
// we emit — measuring the unrounded value let results land a hair under 4.5:1.
const mixWithWhite = (c: Rgb, amount: number): Rgb => ({
  r: clamp255(c.r + (255 - c.r) * amount),
  g: clamp255(c.g + (255 - c.g) * amount),
  b: clamp255(c.b + (255 - c.b) * amount),
  a: 1,
});

const NEUTRAL_TEXT = parseColor(DARK_NEUTRALS.text)!;

// Lighten toward white in small steps until the colour clears `target` on `bg`.
// Steps (not a closed-form solve) keep it obviously correct; at most 20 iterations.
const lightenUntil = (c: Rgb, bg: Rgb, target: number): Rgb => {
  for (let step = 0.05; step <= 1; step += 0.05) {
    const candidate = mixWithWhite(c, step);
    if (contrastRatio(candidate, bg) >= target) return candidate;
  }
  return NEUTRAL_TEXT;
};

/** True for colours that read as a light background (white, cream, light grey…). */
export const isLightSurface = (color?: string | null) => {
  const c = parseColor(color);
  return !!c && c.a > 0.5 && relativeLuminance(c) > LIGHT_SURFACE_LUMINANCE;
};

/**
 * Dark-mode value for a merchant SURFACE colour. Light (or unparseable) surfaces become
 * `fallback`; transparent stays transparent; dark/saturated brand surfaces are kept.
 */
export const darkSurface = (color: string | null | undefined, fallback: string) => {
  const value = color?.trim();
  if (value === "transparent") return value;
  const c = parseColor(value);
  if (!c) return fallback;
  if (c.a === 0) return value!;
  return relativeLuminance(c) > LIGHT_SURFACE_LUMINANCE ? fallback : value!;
};

/** Dark-mode value for a merchant TEXT colour drawn on `bg`. */
export const darkText = (
  color: string | null | undefined,
  bg: string = DARK_NEUTRALS.pageBg,
) => {
  const c = parseColor(color);
  const background = parseColor(bg) ?? parseColor(DARK_NEUTRALS.pageBg)!;
  if (!c) return DARK_NEUTRALS.text;
  if (contrastRatio(c, background) >= TEXT_CONTRAST) return color!;
  if (chroma(c) < CHROMA_THRESHOLD) return DARK_NEUTRALS.text;
  return toHex(lightenUntil(c, background, TEXT_CONTRAST));
};

/**
 * Dark-mode value for a BRAND fill (button/accent) plus its inner (label) colour.
 * Kept untouched when it already stands out from the dark page; otherwise a chromatic
 * brand is lightened (hue preserved) and an achromatic one (black/charcoal brands)
 * inverts to light, and the label is re-picked for contrast against the new fill.
 */
export const darkBrand = (
  color: string | null | undefined,
  inner: string | null | undefined,
) => {
  const c = parseColor(color);
  const page = parseColor(DARK_NEUTRALS.pageBg)!;
  if (!c || c.a === 0 || contrastRatio(c, page) >= FILL_CONTRAST) {
    return { color: color ?? "", inner: inner ?? "" };
  }
  const adjusted =
    chroma(c) < CHROMA_THRESHOLD
      ? NEUTRAL_TEXT
      : lightenUntil(c, page, TEXT_CONTRAST);
  return { color: toHex(adjusted), inner: readableOn(adjusted, inner) };
};

/** `preferred` if it reads on `bg`, else whichever of near-black/white reads better. */
const readableOn = (bg: Rgb, preferred?: string | null) => {
  const p = parseColor(preferred);
  if (p && contrastRatio(p, bg) >= TEXT_CONTRAST) return preferred!;
  const onLight = parseColor(DARK_NEUTRALS.onLight)!;
  const onDark = parseColor(DARK_NEUTRALS.onDark)!;
  return contrastRatio(onLight, bg) >= contrastRatio(onDark, bg)
    ? DARK_NEUTRALS.onLight
    : DARK_NEUTRALS.onDark;
};

/**
 * Inline vars for a merchant-configured SURFACE colour (builder blocks), consumed by
 * the `.themed-bg` class: light mode paints the colour as-is, dark mode paints its
 * `darkSurface` counterpart — so a white/cream tile goes dark under light text, while a
 * saturated brand tile is kept. Empty when the merchant set no colour.
 */
export const getThemedBgVars = (
  color?: string | null,
): Record<"--themed-bg" | "--themed-bg-dark", string> | Record<string, never> =>
  color
    ? {
        "--themed-bg": color,
        "--themed-bg-dark": darkSurface(color, DARK_NEUTRALS.surface),
      }
    : {};
